import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PaintBrush, Plus, ArrowSquareOut, DownloadSimple, CircleNotch, LinkBreak } from "@phosphor-icons/react";
import { api, errDetail } from "../lib/api";
import { Sidebar } from "../components/Sidebar";

const DESIGN_TYPES = [
  { id: "presentation", label: "Presentation" },
  { id: "doc", label: "Doc" },
  { id: "whiteboard", label: "Whiteboard" },
  { id: "instagram-post", label: "Instagram Post" },
  { id: "story", label: "Story" },
  { id: "youtube-thumbnail", label: "YouTube Thumbnail" },
];
const FORMATS = ["png", "jpg", "pdf", "pptx"];

export default function CanvaPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [designType, setDesignType] = useState("presentation");
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [fmt, setFmt] = useState({});
  const [errReason, setErrReason] = useState("");

  const loadStatus = async () => {
    const { data } = await api.get("/canva/status");
    setStatus(data);
    if (data.connected) loadDesigns();
  };

  const loadDesigns = async () => {
    setLoadingDesigns(true);
    try {
      const { data } = await api.get("/canva/designs");
      setDesigns(data);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setLoadingDesigns(false);
    }
  };

  useEffect(() => {
    const flag = params.get("canva");
    if (flag === "connected") toast.success("Canva connected!");
    if (flag === "error") {
      const reason = params.get("reason") || "";
      setErrReason(reason);
      toast.error(reason ? `Canva: ${reason}` : "Canva connection failed — try again");
    }
    loadStatus().catch(() => setStatus({ connected: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.get("/canva/connect");
      window.location.href = data.url;
    } catch (e) { toast.error(errDetail(e)); }
  };

  const disconnect = async () => {
    await api.post("/canva/disconnect");
    setStatus({ connected: false });
    setDesigns([]);
    toast.success("Canva disconnected");
  };

  const createDesign = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/canva/designs", { design_type: designType, title: title || "Triad Design" });
      toast.success("Design created — opening in Canva");
      if (data.edit_url) window.open(data.edit_url, "_blank");
      setTitle("");
      loadDesigns();
    } catch (e) { toast.error(errDetail(e)); } finally { setCreating(false); }
  };

  const exportDesign = async (d) => {
    const format = fmt[d.id] || "png";
    setExporting(d.id);
    try {
      const { data } = await api.post("/canva/export", { design_id: d.id, format }, { timeout: 120000 });
      if (data.asset_id) toast.success(`Exported as ${format.toUpperCase()} — saved to your Library gallery`);
      else toast.success(`Exported as ${format.toUpperCase()}`);
      if (data.download_url) window.open(data.download_url, "_blank");
    } catch (e) { toast.error(errDetail(e)); } finally { setExporting(null); }
  };

  const inputCls = "bg-[var(--bg)] border border-[var(--border-strong)] rounded-xl px-3.5 py-2.5 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] transition-colors duration-300 placeholder:text-[var(--muted)]";

  return (
    <div className="flex h-screen bg-[var(--bg)] overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto px-10 py-10">
        <h1 className="font-serif-display font-semibold text-3xl sm:text-4xl tracking-tight mb-2">Canva</h1>
        <p className="text-sm text-[var(--text-2)] mb-8">Create and export Canva designs without leaving Triad.</p>

        {status === null && <p className="text-sm text-[var(--muted)] animate-pulse">Checking connection…</p>}

        {status && !status.connected && (
          <div className="max-w-md">
            {errReason && (
              <div className="mb-5 bg-[var(--error)]/8 border border-[var(--error)]/25 rounded-2xl p-5 fade-up" data-testid="canva-error-hint">
                <p className="text-[13px] font-semibold text-[var(--error)] mb-1.5">Canva said: “{errReason}”</p>
                {errReason.toLowerCase().includes("scope") ? (
                  <div className="text-[12.5px] text-[var(--text-2)] leading-relaxed">
                    <p className="mb-2">Your Canva integration is missing permissions. To fix:</p>
                    <ol className="list-decimal pl-4 space-y-1">
                      <li>Open <a href="https://www.canva.com/developers/integrations" target="_blank" rel="noreferrer" className="text-[var(--accent)] underline">canva.com/developers/integrations</a> → your integration</li>
                      <li>Go to the <b>Scopes</b> tab and enable: <b>asset</b> (read + write), <b>design:content</b> (read + write), <b>design:meta</b> (read), <b>profile</b> (read)</li>
                      <li>Save, then click <b>Connect Canva</b> below again</li>
                    </ol>
                  </div>
                ) : (
                  <p className="text-[12.5px] text-[var(--text-2)]">Please try connecting again. If it keeps failing, check the integration settings on canva.dev.</p>
                )}
              </div>
            )}
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-10 text-center fade-up" style={{ boxShadow: "var(--shadow)" }} data-testid="canva-connect-card">
            <span className="inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-5" style={{ background: "#8B3DFF" }}>
              <PaintBrush size={26} weight="fill" className="text-white" />
            </span>
            <h2 className="font-serif-display font-semibold text-xl mb-2">Connect your Canva account</h2>
            <p className="text-[13px] text-[var(--text-2)] mb-6">Create designs, open them in the Canva editor, and export PNG / JPG / PDF / PPTX straight into your Library.</p>
            <button data-testid="canva-connect-button" onClick={connect}
              className="rounded-full text-white font-semibold text-sm px-8 py-3 transition-colors duration-300" style={{ background: "#8B3DFF" }}>
              Connect Canva
            </button>
            </div>
          </div>
        )}

        {status?.connected && (
          <>
            <div className="flex items-center justify-between max-w-3xl mb-8">
              <p className="text-[13px] text-[var(--text-2)]" data-testid="canva-connected-status">
                Connected{status.display_name ? ` as ${status.display_name}` : ""} <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 ml-1" />
              </p>
              <button data-testid="canva-disconnect-button" onClick={disconnect}
                className="flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--error)] transition-colors duration-300">
                <LinkBreak size={13} /> Disconnect
              </button>
            </div>

            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-5 max-w-3xl mb-8 flex flex-wrap items-center gap-3" style={{ boxShadow: "var(--shadow)" }} data-testid="canva-create-row">
              <select data-testid="canva-design-type" className={inputCls} value={designType} onChange={(e) => setDesignType(e.target.value)}>
                {DESIGN_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <input data-testid="canva-design-title" className={`${inputCls} flex-1 min-w-[180px]`} placeholder="Design title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
              <button data-testid="canva-create-button" onClick={createDesign} disabled={creating}
                className="flex items-center gap-2 rounded-full text-white text-[13px] font-semibold px-5 py-2.5 transition-colors duration-300 disabled:opacity-50" style={{ background: "var(--accent)" }}>
                {creating ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} Create in Canva
              </button>
            </div>

            <div className="flex items-center justify-between max-w-3xl mb-4">
              <h2 className="font-serif-display font-semibold text-lg">Your designs</h2>
              {loadingDesigns && <CircleNotch size={16} className="animate-spin text-[var(--muted)]" />}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl" data-testid="canva-designs-grid">
              {!loadingDesigns && designs.length === 0 && <p className="text-sm text-[var(--muted)] col-span-full">No designs yet — create one above.</p>}
              {designs.map((d) => (
                <div key={d.id} data-testid={`canva-design-${d.id}`} className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
                  <div className="h-36 bg-[var(--surface-2)] flex items-center justify-center overflow-hidden">
                    {d.thumbnail ? <img src={d.thumbnail} alt={d.title} className="w-full h-full object-cover" /> : <PaintBrush size={28} className="text-[var(--muted)]" />}
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-medium text-[var(--text)] truncate mb-3">{d.title}</p>
                    <div className="flex items-center gap-2">
                      {d.edit_url && (
                        <a href={d.edit_url} target="_blank" rel="noreferrer" data-testid={`canva-open-${d.id}`}
                          className="flex items-center gap-1 text-xs font-medium text-[var(--accent)] hover:underline">
                          <ArrowSquareOut size={13} /> Open
                        </a>
                      )}
                      <span className="ml-auto flex items-center gap-1.5">
                        <select data-testid={`canva-format-${d.id}`} value={fmt[d.id] || "png"} onChange={(e) => setFmt({ ...fmt, [d.id]: e.target.value })}
                          className="bg-[var(--bg)] border border-[var(--border)] rounded-lg text-[11px] px-1.5 py-1 outline-none">
                          {FORMATS.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                        <button data-testid={`canva-export-${d.id}`} onClick={() => exportDesign(d)} disabled={exporting === d.id} title="Export"
                          className="text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--hover)] p-1.5 rounded-lg transition-colors duration-300 disabled:opacity-40">
                          {exporting === d.id ? <CircleNotch size={15} className="animate-spin" /> : <DownloadSimple size={15} />}
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
